import {
  isStale as isStaleFor,
  expiryOf as expiryFor,
  type Fact,
  type FactDefinition,
  type StaleFact as StaleFactOf,
} from '@asvise/facts'

/**
 * Approved vocabulary for the household protection engine.
 *
 * The commercial engine's sibling, and deliberately not its descendant. What
 * they share is `@asvise/facts`: what a fact is, how two accounts of one field
 * are ranked, when an answer expires, and the merge that turns rules'
 * assertions into one answer. Everything in this file is what a household is,
 * and none of it appears over there.
 *
 * Three differences are worth stating at the top, because each of them is a
 * decision rather than a translation.
 *
 * SEVEN STATUSES, not four. The framework separates "we could not verify this"
 * from "there is a gap" from "this needs a person to look at it", and a
 * household needs all three: a family reading a result has to know whether the
 * answer is about their policies or about our confidence in reading them.
 *
 * FIVE KINDS OF RULE AUTHORITY, not three. Between law and our own heuristics
 * sit public schemes and official guidance, and those are the two that a
 * consumer product must never blur: MediShield Life is an entitlement, the
 * "nine times annual income" benchmark is education, and an Asvise threshold is
 * neither. The commercial register had no need to tell those apart.
 *
 * TWO SUBJECTS, not one. A company is one legal person; a household is several
 * real ones. Cash flow, savings and a budget belong to the household, while a
 * benefit belongs to a named individual, and the assessment has to hold both.
 */

/**
 * Which rules produced an assessment, stamped on every saved one.
 *
 * Its own, and never the commercial engine's: two products that shared a
 * version string would be unable to say which of them had changed. It moves
 * whenever a rule could reach a different verdict on unchanged answers.
 *
 * The date the rules last changed, YYYY.MM.DD.
 */
export const RULE_SET_VERSION = '2026.10.01'

/**
 * Which country module decided the thresholds, stamped beside the rules.
 *
 * Separate from the rule set because framework section 11 asks for exactly this
 * separation: the engine is meant to be jurisdiction-independent and a country
 * module supplies the schemes, the benchmarks, the currency and the
 * affordability assumptions. One version string could not say which of the two
 * had moved, and an assessment reproducible only if both are known needs both
 * recorded.
 *
 * There is one module, and it is Singapore's. Saying so in a version string is
 * what makes the second one a visible change rather than a silent one.
 */
export const COUNTRY_MODULE_VERSION = 'SG-2026.10.01'

/**
 * Which question set the answers were given to.
 *
 * Framework section 10.3 calls it the household profile version, and it answers
 * a question the rule set version cannot: whether a stored assessment is
 * missing a field because the household declined it or because nobody was
 * asked. It moves when a field is added to or removed from either registry
 * below, not when its wording changes.
 */
export const PROFILE_SCHEMA_VERSION = '2026.10.01'

/** The market this rule set is written for. There is exactly one today. */
export const COUNTRY = 'SG' as const
export type Country = typeof COUNTRY

export {
  DEFAULT_RANK,
  FACT_SOURCES,
  RANK_LABEL,
  SOURCE_RANK,
  UNKNOWN,
  factOf,
  preferFact,
  statedByUser,
  valueOf,
  type Citation,
  type Confidence,
  type Fact,
  type FactDefinition,
  type FactSource,
  type Origin,
  type Provenance,
  type SourceRank,
  type Unknown,
} from '@asvise/facts'

/**
 * The seven statuses a protection domain may carry.
 *
 * Framework section 7.3, and the order below is the precedence in section 7.4
 * read top to bottom. Never "you are protected": the strongest thing this
 * engine may say is that no gap was obvious from what it was shown.
 */
export const STATUSES = [
  'HUMAN_REVIEW_REQUIRED',
  'DATA_INCOMPLETE',
  'COVERAGE_NOT_VERIFIED',
  'ATTENTION_REQUIRED',
  'PARTIALLY_ADDRESSED',
  'NO_OBVIOUS_GAP',
  'NOT_APPLICABLE',
] as const
export type Status = (typeof STATUSES)[number]

/**
 * Strongest first. Applied only after domain rules have run, so a contradiction
 * between a schedule and an endorsement reaches human review however large the
 * benefit it names.
 */
export const STATUS_PRECEDENCE: readonly Status[] = STATUSES

/**
 * The pair that cannot both be true of one domain.
 *
 * Passed to `decideStatus` rather than known by it. "A material exposure with no
 * verified protection" and "no material gap was found" are the two ends of this
 * register; a different register opposes a different pair.
 */
export const CONTRADICTS: readonly [Status, Status] = ['ATTENTION_REQUIRED', 'NO_OBVIOUS_GAP']

/**
 * What the explanation layer may not say. Framework section 9.3's deny list.
 *
 * One list, because there were two: the engine's test checked five phrases and
 * the interface's checked nine, which is two hand-maintained copies of a
 * normative list and they had already drifted. The first five are section 9.3's
 * own words; the rest are near forms of the same claims that the interface test
 * had added and that are worth keeping.
 *
 * Checked against serialised output rather than against source, so it catches a
 * phrase arriving in a reason code, a translation or a rule's prose. Note the
 * hazard this shares with the bundle guard's markers: a sentence *denying* one
 * of these contains it, so copy that says "we never say you are fully
 * protected" would fail. That is the right trade here, because the denial is
 * easy to reword and the claim is not.
 */
export const FORBIDDEN_PHRASES: readonly string[] = [
  'you must buy',
  'suitable for you',
  'switch or cancel',
  'fully protected',
  'claim payment is guaranteed',
  'guaranteed to be paid',
  'you should buy',
  'cancel your',
  'replace your',
  'we recommend',
]

export const STATUS_LABEL: Record<Status, string> = {
  HUMAN_REVIEW_REQUIRED: 'Needs a person to look at it',
  DATA_INCOMPLETE: 'More information needed',
  COVERAGE_NOT_VERIFIED: 'Cover not verified',
  ATTENTION_REQUIRED: 'Requires attention',
  PARTIALLY_ADDRESSED: 'Partly addressed',
  NO_OBVIOUS_GAP: 'No obvious gap',
  NOT_APPLICABLE: 'Does not apply',
}

/**
 * Where a rule's authority comes from. Framework section 10.2.
 *
 * The interface must never present an Asvise heuristic as a legal requirement,
 * and the only way to keep that promise is for every rule to say which it is.
 * The two in the middle are the ones a commercial screening never needed:
 * a public scheme is an entitlement somebody has, and official guidance is
 * education somebody published. Neither is law and neither is ours.
 */
export const RULE_SOURCES = [
  'LAW',
  'PUBLIC_SCHEME',
  'POLICY',
  'PUBLIC_GUIDANCE',
  'ASVISE_SCREENING',
] as const
export type RuleSource = (typeof RULE_SOURCES)[number]

export const RULE_SOURCE_LABEL: Record<RuleSource, string> = {
  LAW: 'Law or regulation',
  PUBLIC_SCHEME: 'A national scheme',
  POLICY: 'Your own policy',
  PUBLIC_GUIDANCE: 'Official public guidance',
  ASVISE_SCREENING: 'Asvise screening logic',
}

/** The six protection domains. Framework section 7.1. */
export const DOMAINS = [
  'EMERGENCY_LIQUIDITY',
  'HOSPITAL_SURGICAL',
  'DEATH_INCOME_CONTINUITY',
  'DISABILITY_TPD',
  'CRITICAL_ILLNESS_RECOVERY',
  'LONG_TERM_CARE',
] as const
export type Domain = (typeof DOMAINS)[number]

export const DOMAIN_LABEL: Record<Domain, string> = {
  EMERGENCY_LIQUIDITY: 'Emergency savings',
  HOSPITAL_SURGICAL: 'Hospital and surgery',
  DEATH_INCOME_CONTINUITY: 'Family income if someone dies',
  DISABILITY_TPD: 'Permanent disability',
  CRITICAL_ILLNESS_RECOVERY: 'Income during a serious illness',
  LONG_TERM_CARE: 'Long-term care',
}

/**
 * Who each domain is about, which the framework asserts as a principle in
 * section 2 and then never carries into a data structure.
 *
 * Three values rather than two, and the third is the interesting one. A
 * household's emergency savings belong to the household and to nobody in it. A
 * hospital policy insures a named person and says nothing about anyone else.
 * Family income after a death is both: the exposure is the household's, and the
 * cover attaches to whichever individual the schedule names.
 *
 * Without this table the assessment has to guess, and the guess that gets made
 * is "the household has life cover" about a household where one of two earners
 * does.
 */
export type DomainSubject = 'HOUSEHOLD' | 'PERSON' | 'HOUSEHOLD_EXPOSURE_PERSON_COVER'

export const DOMAIN_SUBJECT: Record<Domain, DomainSubject> = {
  EMERGENCY_LIQUIDITY: 'HOUSEHOLD',
  HOSPITAL_SURGICAL: 'PERSON',
  DEATH_INCOME_CONTINUITY: 'HOUSEHOLD_EXPOSURE_PERSON_COVER',
  DISABILITY_TPD: 'HOUSEHOLD_EXPOSURE_PERSON_COVER',
  CRITICAL_ILLNESS_RECOVERY: 'HOUSEHOLD_EXPOSURE_PERSON_COVER',
  LONG_TERM_CARE: 'PERSON',
}

/**
 * How far a domain is built.
 *
 * `OFF` produces no finding at all and is reported as out of scope, which is a
 * statement about this rule set and never about the household. The same rung
 * ladder the commercial engine uses, and for the same reason: a domain nobody
 * has written rules for must not read as a clean bill.
 */
export type DomainStage = 'OFF' | 'SCREEN_ONLY' | 'FULL'

export const DOMAIN_STAGE: Record<Domain, DomainStage> = {
  EMERGENCY_LIQUIDITY: 'FULL',
  HOSPITAL_SURGICAL: 'FULL',
  DEATH_INCOME_CONTINUITY: 'FULL',
  /**
   * Screened and never concluded, on purpose.
   *
   * A TPD sum is readable off a schedule, and what it would have to be enough
   * for is a disability expense, care and income-interruption scenario this
   * questionnaire does not ask about. So the rules report the benefit and stop
   * at DATA_INCOMPLETE rather than lending the death result to it, which is the
   * specific mistake the demo scenario calls out.
   */
  DISABILITY_TPD: 'FULL',
  CRITICAL_ILLNESS_RECOVERY: 'FULL',
  LONG_TERM_CARE: 'OFF',
}

/** Who somebody is to the household. Identity, not a fact, and never ranked. */
export const PERSON_ROLES = ['SELF', 'PARTNER', 'CHILD', 'PARENT', 'OTHER_DEPENDENT'] as const
export type PersonRole = (typeof PERSON_ROLES)[number]

export const PERSON_ROLE_LABEL: Record<PersonRole, string> = {
  SELF: 'You',
  PARTNER: 'Partner',
  CHILD: 'Child',
  PARENT: 'Parent',
  OTHER_DEPENDENT: 'Other dependant',
}

/**
 * An opaque id for a member of the household.
 *
 * Never a name, and this is the lesson the commercial engine learned the
 * expensive way: a schedule writes "Tan Wei Ming (S1234567A)" where a person
 * writes the name alone, those two were compared as identifiers, and the finding
 * was a gap asserted against a policy that plainly named them.
 *
 * Here the stakes are higher, because the whole assessment turns on which member
 * a benefit belongs to. So a person has an id the household never types, a label
 * they do, and the join between an extracted insured person and one of these is
 * made by a human and stored as their answer. See `insured_person_id` on a
 * benefit.
 */
export type PersonId = string

export type YesNoUnknown = 'YES' | 'NO' | 'UNKNOWN'

/**
 * Where a person stands in the jurisdiction the screening is configured for.
 *
 * Framework question A1, which asks where the household lives and is
 * financially based, and which was the one question of the twenty that nothing
 * covered.
 *
 * NO RULE READS THIS, DELIBERATELY. It was added to drive a national scheme
 * baseline, and that baseline was built and reverted: the demo scenario says in
 * terms that no public-scheme entitlement is inferred from residence alone, and
 * reading an entitlement off this field is exactly that inference. Section 11
 * makes schemes the business of a country module, and a verified one would take
 * this as an input; until there is one, the answer is recorded and nothing is
 * concluded from it.
 *
 * Two values rather than three, because citizen and permanent resident are the
 * same answer to the question being asked and telling them apart would collect
 * a distinction nothing uses. Section 4.3 is about collecting less.
 */
export type Residency = 'CITIZEN_OR_PR' | 'OTHER' | 'UNKNOWN'

export const INCOME_STABILITY = ['STABLE', 'VARIABLE', 'SELF_EMPLOYED', 'CONTRACT', 'UNKNOWN'] as const
export type IncomeStability = (typeof INCOME_STABILITY)[number]

export const LIFE_EVENTS = [
  'NONE',
  'MARRIAGE',
  'CHILDBIRTH',
  'HOME_PURCHASE',
  'JOB_CHANGE',
  'CAREGIVING',
  'BEREAVEMENT',
] as const
export type LifeEvent = (typeof LIFE_EVENTS)[number]

/**
 * What the household is asked about itself.
 *
 * Household-scoped, every one of them: money coming in, money going out, what is
 * saved, what is owed, and what could be spared. Duplicating any of this onto a
 * person is how two members end up disagreeing about one household's expenses.
 */
export interface HouseholdFacts {
  household_income_monthly: Fact<number> | null
  essential_expenses_monthly: Fact<number> | null
  existing_premiums_monthly: Fact<number> | null
  /**
   * Everything else the household spends, beyond essentials and premiums.
   *
   * Missing at first, and the surplus came out at S\u00241,500 against the
   * scenario's S\u0024300. A household's spare money is what is left after what
   * it actually spends, not after what it calls essential, and the gap between
   * those two is exactly the money a screening would otherwise invite somebody
   * to commit to a premium.
   */
  other_spending_monthly: Fact<number> | null
  liquid_savings: Fact<number> | null
  total_debt: Fact<number> | null
  education_commitments: Fact<number> | null
  income_stability: Fact<IncomeStability> | null
  sustainable_protection_budget: Fact<number> | null
  recent_life_event: Fact<LifeEvent> | null
  /**
   * How long the household chooses to be supported for, in years.
   *
   * A choice the household makes, not a number the engine knows. The demo picks
   * ten years, and the result says so: a screening that silently assumed a
   * horizon would be presenting its own assumption as the family's need.
   */
  support_horizon_years: Fact<number> | null
  /**
   * What the survivors would actually spend each month.
   *
   * Asked separately from essential expenses because they are not the same
   * figure, and because the difference is where double counting starts. Loan
   * repayments come out of this one when the debt is settled from the benefit.
   */
  survivor_expenses_monthly: Fact<number> | null
  /** Months of a recovery the household wants to be able to fund. Their choice. */
  recovery_period_months: Fact<number> | null
  recovery_additional_costs: Fact<number> | null
  /**
   * Savings the household would put towards a protection need.
   *
   * Zero is a real and common answer, and it is not the same as having no
   * savings: money reserved for emergencies is not available to a death or an
   * illness scenario as well. Allocating one pot to two needs is the arithmetic
   * error the demo scenario names in its acceptance checks.
   */
  savings_allocated_to_protection: Fact<number> | null
  final_and_transition_costs: Fact<number> | null
}

/**
 * What one member is asked about themselves, and no more.
 *
 * Data minimisation is a design constraint here rather than a nicety. There is
 * no health field of any kind: framework section 4.3 keeps diagnoses out of the
 * prototype, and a declaration belongs in an insurer's authorised application.
 *
 * `birth_year` rather than an age, and rather than an age band. An age is stale
 * the day after it is given and a band is stale the moment the person crosses
 * it, where a birth year never rots. The commercial registry taught that a
 * staleness policy is the wrong tool for a fact that could simply be stored in
 * a form that stays true.
 */
export interface PersonFacts {
  birth_year: Fact<number> | null
  /** Whether this person earns. Not how much: the household figure plus a share does that work. */
  earns: Fact<YesNoUnknown> | null
  /** This person's share of household income, 0 to 1. */
  income_share: Fact<number> | null
  /** Whether anybody depends on this person financially. */
  has_dependants: Fact<YesNoUnknown> | null
  /**
   * Where this person stands in the jurisdiction, recorded and not acted on.
   *
   * The only fact here that no rule reads. See `Residency`: inferring a scheme
   * entitlement from it is the thing the demo scenario forbids by name, so it
   * is held for the country module that would verify one.
   */
  residency: Fact<Residency> | null
  employer_life_cover: Fact<YesNoUnknown> | null
  employer_medical_cover: Fact<YesNoUnknown> | null
  /** Whether employer cover would survive a job change. Non-portable unless evidence says otherwise. */
  employer_cover_portable: Fact<YesNoUnknown> | null
}

/** A member of the household: an identity, and what is known about them. */
export interface Person {
  id: PersonId
  /** What the household calls them. Display only; no rule reads it. */
  label: string
  role: PersonRole
  facts: PersonFacts
}

export type HouseholdFactField = keyof HouseholdFacts
export type PersonFactField = keyof PersonFacts

export const HOUSEHOLD_FACT_REGISTRY = {
  household_income_monthly: {
    question: 'B7',
    label: 'Monthly take-home income',
    staleness_months: 12,
  },
  essential_expenses_monthly: {
    question: 'B9',
    label: 'Essential monthly expenses',
    staleness_months: 12,
  },
  existing_premiums_monthly: {
    question: 'B9',
    label: 'Insurance premiums you already pay',
    staleness_months: 12,
  },
  other_spending_monthly: {
    question: 'B9',
    label: 'Everything else you spend each month',
    staleness_months: 12,
  },
  liquid_savings: { question: 'C12', label: 'Savings you could reach quickly', staleness_months: 6 },
  total_debt: { question: 'B10', label: 'What you still owe', staleness_months: 12 },
  education_commitments: {
    question: 'B11',
    label: 'Planned education costs',
    staleness_months: 24,
  },
  income_stability: { question: 'C14', label: 'How steady the income is', staleness_months: 12 },
  sustainable_protection_budget: {
    question: 'C15',
    label: 'What you could comfortably set aside',
    // The shortest life here. A budget is a statement about this month's cash
    // flow, and last year's answer says nothing about whether S$150 is still
    // spare.
    staleness_months: 6,
  },
  recent_life_event: { question: 'D19', label: 'A recent change at home', staleness_months: 12 },
  support_horizon_years: {
    question: 'C13',
    label: 'How long the family should be supported',
    // A choice rather than a measurement, so it does not go out of date on its
    // own. It changes when the household changes it.
    staleness_months: null,
  },
  survivor_expenses_monthly: {
    question: 'C13',
    label: 'What the family would spend without that income',
    staleness_months: 12,
  },
  recovery_period_months: {
    question: 'C13',
    label: 'How long a recovery to plan for',
    staleness_months: null,
  },
  recovery_additional_costs: {
    question: 'C13',
    label: 'Extra costs during a recovery',
    staleness_months: 24,
  },
  savings_allocated_to_protection: {
    question: 'C12',
    label: 'Savings you would put towards this',
    staleness_months: 6,
  },
  final_and_transition_costs: {
    question: 'C13',
    label: 'Final and transition costs',
    staleness_months: 24,
  },
} satisfies Record<HouseholdFactField, FactDefinition>

export const PERSON_FACT_REGISTRY = {
  // Never expires, which is the whole reason it is a year and not an age.
  birth_year: { question: 'A2', label: 'Year of birth', staleness_months: null },
  earns: { question: 'B8', label: 'Earns an income', staleness_months: 12 },
  income_share: { question: 'B8', label: 'Share of family income', staleness_months: 12 },
  has_dependants: { question: 'A4', label: 'Has people depending on them', staleness_months: 12 },
  residency: {
    question: 'A1',
    /**
     * Named for what it asks rather than for what it might decide.
     *
     * It was "Covered by the national health scheme", which stated a conclusion
     * in the question and was refused by the data-minimisation test for
     * containing the word health. The test is blunt and was right anyway:
     * nobody is being asked anything about their health, and a label reading as
     * though they are is a label worth changing.
     */
    label: 'Citizen or permanent resident of Singapore',
    // Two years. It does not change often, and the change that matters is one
    // direction only: somebody who was not covered becoming covered.
    staleness_months: 24,
  },
  employer_life_cover: {
    question: 'D16',
    label: 'Life cover through work',
    staleness_months: 12,
  },
  employer_medical_cover: {
    question: 'D16',
    label: 'Medical cover through work',
    staleness_months: 12,
  },
  employer_cover_portable: {
    question: 'D17',
    label: 'Whether work cover would continue',
    staleness_months: 12,
  },
} satisfies Record<PersonFactField, FactDefinition>

export const HOUSEHOLD_FACT_FIELDS = Object.keys(
  HOUSEHOLD_FACT_REGISTRY,
) as HouseholdFactField[]
export const PERSON_FACT_FIELDS = Object.keys(PERSON_FACT_REGISTRY) as PersonFactField[]

export function emptyHouseholdFacts(): HouseholdFacts {
  return {
    household_income_monthly: null,
    essential_expenses_monthly: null,
    existing_premiums_monthly: null,
    other_spending_monthly: null,
    liquid_savings: null,
    total_debt: null,
    education_commitments: null,
    income_stability: null,
    sustainable_protection_budget: null,
    recent_life_event: null,
    support_horizon_years: null,
    survivor_expenses_monthly: null,
    recovery_period_months: null,
    recovery_additional_costs: null,
    savings_allocated_to_protection: null,
    final_and_transition_costs: null,
  }
}

export function emptyPersonFacts(): PersonFacts {
  return {
    birth_year: null,
    earns: null,
    income_share: null,
    has_dependants: null,
    residency: null,
    employer_life_cover: null,
    employer_medical_cover: null,
    employer_cover_portable: null,
  }
}

/** Everything one household's assessment is computed from. */
export interface Answers extends HouseholdFacts {
  /** Display only; no rule reads it. */
  household_name: string
  /**
   * Who is in the household.
   *
   * Not a Fact, because it is identity rather than an observation: a member does
   * not become stale, they leave, and that is a different event with a different
   * consequence. What is known *about* each of them is Facts, on `Person.facts`.
   */
  people: Person[]
  policies: import('./policy.ts').Policy[]
}

export function emptyAnswers(): Answers {
  return {
    household_name: '',
    people: [],
    policies: [],
    ...emptyHouseholdFacts(),
  }
}

/** Expiry for a household field. The arithmetic is core; the policy is ours. */
export function isHouseholdFactStale<T>(
  f: Fact<T>,
  field: HouseholdFactField,
  now: number,
): boolean {
  return isStaleFor(f, HOUSEHOLD_FACT_REGISTRY[field].staleness_months, now)
}

export function isPersonFactStale<T>(f: Fact<T>, field: PersonFactField, now: number): boolean {
  return isStaleFor(f, PERSON_FACT_REGISTRY[field].staleness_months, now)
}

export function householdExpiryOf<T>(f: Fact<T>, field: HouseholdFactField): number | null {
  return expiryFor(f, HOUSEHOLD_FACT_REGISTRY[field].staleness_months)
}

/** A fact the engine stopped trusting. Carries the person where there is one. */
export type StaleFact = StaleFactOf<HouseholdFactField | PersonFactField> & {
  person_id?: PersonId
}

export const DISCLAIMER =
  'An educational screening based on the information provided. Not financial advice, not a recommendation to buy, change or cancel any policy, and not a statement that any claim would be paid.'
